/** The games a style's images can be sampled from. */
import { CasinoGame } from "../../../http/protocol";
import { AssetSample } from "./assetSample";
import { ThumbnailStyle } from "./centralImagesDocument";

/** Enough games that one oddly sized asset shows up as a disagreement rather than as the answer, few enough to stay one round trip. */
export const SAMPLE_LIMIT = 4;

/**
 * Central images are scoped to particular integrations and studios, so the first handful of games
 * routinely have no asset in the style being configured, and a window the size of the sample would
 * find nothing to measure.
 */
export const CANDIDATE_LIMIT = 50;

/** A game a sample can be drawn from, with the styles it actually has an image in. */
export interface SampleCandidate {
  sample: AssetSample;
  variants: string[];
}

/** The admin protocol carries `imageVariants` as the one comma-separated string the entity stores, where the player protocol carries it already split. */
export function toCandidates(games: CasinoGame[]): SampleCandidate[] {
  return games
    .map((game) => ({
      sample: { game: game.name ?? "", studio: game.studio?.name ?? "" },
      variants: (game.imageVariants ?? "").split(",").filter((variant) => variant !== ""),
    }))
    .filter((candidate) => candidate.sample.game !== "" && candidate.sample.studio !== "" && candidate.variants.length > 0);
}

/**
 * Only a game whose `imageVariants` names the style has an image in it — the same rule the players'
 * lobby applies in `imageUrlForVariant` (`frontend/player-frontend/src/casino/CasinoActions.ts`) —
 * so any other game would measure as a missing asset and preview as a broken tile for a template
 * that is in fact correct.
 */
export function samplesForStyle(candidates: SampleCandidate[], style: ThumbnailStyle | undefined): AssetSample[] {
  if (style === undefined) {
    return [];
  }
  return candidates
    .filter((candidate) => candidate.variants.indexOf(style) !== -1)
    .slice(0, SAMPLE_LIMIT)
    .map((candidate) => candidate.sample);
}

/**
 * Which images a reading covers, as one value that holds still from render to render even though the
 * list of samples behind it does not.
 */
export function sampleSetKeyOf(style: ThumbnailStyle, samples: AssetSample[]): string {
  return [style, ...samples.map((sample) => `${sample.studio}/${sample.game}`)].join("|");
}
